// ─── Référentiel d'ingrédients — CALQUE PERSONNEL ──────────────────
// Par-dessus la base globale (ingredientsBase), jamais modifiée directement —
// ni ses ingrédients, ni ses rayons :
//   • ajouts / masques / modifs         → ingrédients
//   • rayonsAjouts / rayonsMasques / rayonsModifs → catégories (rayons)
// « Supprimer » un élément de la base = le masquer pour soi (réversible) ;
// « modifier » un élément de la base = une correction perso par-dessus
// (réversible aussi). Rien de tout ça n'écrit jamais sur la base partagée.
//   ingredientsPerso/{uid} = {
//     ajouts: [{id,nom,unite,categorie}], masques: [id…],
//     modifs: { [baseId]: {nom,unite,categorie} },
//     rayonsAjouts: [{id,label,emoji}], rayonsMasques: [id…],
//     rayonsModifs: { [baseRayonId]: {label,emoji} },
//     maj }
import { db } from '@mop/core';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';

function nouvelId(prefixe) {
  return prefixe + '_' + Math.random().toString(36).slice(2, 9) + Date.now().toString(36);
}

const VIDE = { ajouts: [], masques: [], modifs: {}, rayonsAjouts: [], rayonsMasques: [], rayonsModifs: {} };

export function ecouterPerso(uid, callback, onErreur) {
  if (!uid) { callback(VIDE); return () => {}; }
  return onSnapshot(doc(db, 'ingredientsPerso', uid),
    s => callback(s.exists() ? { ...VIDE, ...s.data() } : VIDE),
    (e) => { callback(VIDE); onErreur?.(e); });
}

async function enregistrerPerso(uid, perso) {
  if (!uid) return;
  await setDoc(doc(db, 'ingredientsPerso', uid), { ...perso, maj: new Date().toISOString() }, { merge: true });
}

// ── Ajouts personnels (ingrédients) ─────────────────────────────────
export async function ajouterPersonnel(uid, perso, item) {
  const nom = (item.nom || '').trim();
  if (!nom) return;
  const ajouts = [...(perso.ajouts || []), {
    id: nouvelId('ing'), nom, unite: (item.unite || '').trim(), categorie: (item.categorie || '').trim(),
  }];
  await enregistrerPerso(uid, { ...perso, ajouts });
}
export async function modifierAjout(uid, perso, id, item) {
  const ajouts = (perso.ajouts || []).map(i => i.id === id
    ? { ...i, nom: (item.nom || '').trim(), unite: (item.unite || '').trim(), categorie: (item.categorie || '').trim() }
    : i);
  await enregistrerPerso(uid, { ...perso, ajouts });
}
export async function retirerAjout(uid, perso, id) {
  await enregistrerPerso(uid, { ...perso, ajouts: (perso.ajouts || []).filter(i => i.id !== id) });
}

// ── Masquage / correction d'ingrédients de la BASE ──────────────────
export async function masquerBase(uid, perso, id) {
  const masques = perso.masques || [];
  if (masques.includes(id)) return;
  await enregistrerPerso(uid, { ...perso, masques: [...masques, id] });
}
export async function demasquerBase(uid, perso, id) {
  await enregistrerPerso(uid, { ...perso, masques: (perso.masques || []).filter(x => x !== id) });
}
export async function modifierBase(uid, perso, id, item) {
  const modifs = {
    ...(perso.modifs || {}),
    [id]: { nom: (item.nom || '').trim(), unite: (item.unite || '').trim(), categorie: (item.categorie || '').trim() },
  };
  await enregistrerPerso(uid, { ...perso, modifs });
}
export async function annulerModifBase(uid, perso, id) {
  const modifs = { ...(perso.modifs || {}) };
  delete modifs[id];
  await enregistrerPerso(uid, { ...perso, modifs });
}

// ── Vue effective (pure) : base non masquée (corrigée si besoin) + ajouts ──
export function ingredientsEffectifs(base, perso) {
  const masques = new Set(perso?.masques || []);
  const modifs = perso?.modifs || {};
  const baseVisible = (base || [])
    .filter(i => !masques.has(i.id))
    .map(i => modifs[i.id] ? { ...i, ...modifs[i.id], origine: 'base', modifie: true } : { ...i, origine: 'base' });
  const ajouts = (perso?.ajouts || []).map(i => ({ ...i, origine: 'perso' }));
  return [...baseVisible, ...ajouts];
}

// ═══ RAYONS (catégories) — même principe, appliqué aux catégories ═══

export async function ajouterRayon(uid, perso, rayon) {
  const label = (rayon.label || '').trim();
  if (!label) return;
  const rayonsAjouts = [...(perso.rayonsAjouts || []), {
    id: nouvelId('ray'), label, emoji: rayon.emoji || '📦',
  }];
  await enregistrerPerso(uid, { ...perso, rayonsAjouts });
}
export async function modifierRayonAjoute(uid, perso, id, rayon) {
  const rayonsAjouts = (perso.rayonsAjouts || []).map(r => r.id === id
    ? { ...r, label: (rayon.label || '').trim(), emoji: rayon.emoji || '📦' } : r);
  await enregistrerPerso(uid, { ...perso, rayonsAjouts });
}
export async function retirerRayonAjoute(uid, perso, id) {
  await enregistrerPerso(uid, { ...perso, rayonsAjouts: (perso.rayonsAjouts || []).filter(r => r.id !== id) });
}
export async function masquerRayonBase(uid, perso, id) {
  const rayonsMasques = perso.rayonsMasques || [];
  if (rayonsMasques.includes(id)) return;
  await enregistrerPerso(uid, { ...perso, rayonsMasques: [...rayonsMasques, id] });
}
export async function demasquerRayonBase(uid, perso, id) {
  await enregistrerPerso(uid, { ...perso, rayonsMasques: (perso.rayonsMasques || []).filter(x => x !== id) });
}
export async function modifierRayonBase(uid, perso, id, rayon) {
  const rayonsModifs = {
    ...(perso.rayonsModifs || {}),
    [id]: { label: (rayon.label || '').trim(), emoji: rayon.emoji || '📦' },
  };
  await enregistrerPerso(uid, { ...perso, rayonsModifs });
}
export async function annulerModifRayonBase(uid, perso, id) {
  const rayonsModifs = { ...(perso.rayonsModifs || {}) };
  delete rayonsModifs[id];
  await enregistrerPerso(uid, { ...perso, rayonsModifs });
}

// Vue effective des rayons : base (défaut RAYONS si la base n'en a pas
// encore) non masqués (corrigés si besoin) + rayons ajoutés perso.
export function rayonsEffectifs(baseRayons, perso, rayonsParDefaut) {
  const src = (baseRayons && baseRayons.length) ? baseRayons : (rayonsParDefaut || []);
  const masques = new Set(perso?.rayonsMasques || []);
  const modifs = perso?.rayonsModifs || {};
  const baseVisible = src
    .filter(r => !masques.has(r.id))
    .map(r => modifs[r.id] ? { ...r, ...modifs[r.id], origine: 'base', modifie: true } : { ...r, origine: 'base' });
  const ajouts = (perso?.rayonsAjouts || []).map(r => ({ ...r, origine: 'perso' }));
  return [...baseVisible, ...ajouts];
}
